import { useState, useEffect, useContext } from "react";
import { Box, Button, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import {
  Family as FamilyProto,
  UpdateFamilyRequest,
  FamilyStatus,
} from "protogen/advisors_service_pb";
import { BillingCoupon } from "protogen/billing_pb";
import { useGetFamilyDetails, useUpdateFamily } from "services/advisor";
import { PlatformFeeSchedule } from "protogen/billing_pb";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import { isUserAdmin } from "common/userUtils";
import { CurrentUserContext } from "components/context/RequireAuth";
import ChangePlanDialog from "./billing-management/ChangePlanDialog";
import AddHoursDialog from "./billing-management/AddHoursDialog";
import DeactivationDialog from "./billing-management/DeactivationDialog";
import PlatformFeeSelect from "./PlatformFeeSelect";
import CouponSelect from "./CouponSelect";
import useIsMobile from "components/hooks/useIsMobile";
import DropdownButton from "components/common/DropdownButton";

export default ({
  family,
  refresh,
}: {
  family: FamilyProto;
  refresh?: () => void;
}) => {
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [addHoursDialogOpen, setAddHoursDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [localFamily, setLocalFamily] = useState<FamilyProto>(family);
  const [platformFeeSchedule, setPlatformFeeSchedule] = useState<
    PlatformFeeSchedule | undefined
  >(family.platformFeeSchedule);
  const [introCoupon, setIntroCoupon] = useState<BillingCoupon | undefined>(
    family.billingInfo?.introCoupon,
  );
  const [startDate, setStartDate] = useState<Date | undefined>(
    family.startDate ? new Date(family.startDate) : undefined,
  );

  const { request: requestRefreshFamily } = useGetFamilyDetails();
  const { request: requestUpdateFamily, loading: updateLoading } =
    useUpdateFamily();
  const { pushSnack } = useSnackpack();
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = isUserAdmin(currentUser);

  useEffect(() => {
    setLocalFamily(family);
    setPlatformFeeSchedule(family.platformFeeSchedule);
    setIntroCoupon(family.billingInfo?.introCoupon);
    setStartDate(family.startDate ? new Date(family.startDate) : undefined);
  }, [family]);

  const refreshFamily = async () => {
    const refreshedData = await requestRefreshFamily({
      familyRef: family.ref,
    });
    if (refreshedData?.family) {
      setLocalFamily(refreshedData.family);
      setPlatformFeeSchedule(refreshedData.family.platformFeeSchedule);
      setIntroCoupon(refreshedData.family.billingInfo?.introCoupon);
      setStartDate(
        refreshedData.family.startDate
          ? new Date(refreshedData.family.startDate)
          : undefined,
      );
    }
  };

  const onPlanUpdate = async (updatedFamily: FamilyProto) => {
    setLocalFamily(updatedFamily);
    setChangePlanDialogOpen(false);
    await refreshFamily();
  };

  const onAddHoursUpdate = async (updatedFamily: FamilyProto) => {
    setLocalFamily(updatedFamily);
    setAddHoursDialogOpen(false);
    await refreshFamily();
  };

  const onDeactivate = async () => {
    setDeactivateDialogOpen(false);
    if (refresh) {
      refresh();
    } else {
      await refreshFamily();
    }
  };

  const updatePlatformFee = async (fee?: PlatformFeeSchedule) => {
    if (!isAdmin) return;
    setPlatformFeeSchedule(fee);
    const resp = await requestUpdateFamily(
      new UpdateFamilyRequest({
        familyRef: family.ref,
        platformFeeSchedule: fee || PlatformFeeSchedule.SCHEDULE_NOT_SET,
      }),
    );
    if (resp?.family) {
      pushSnack({
        message: "Platform fee updated successfully",
        saveNotification: true,
        key: new Date().getTime(),
      });
      await refreshFamily();
    }
  };

  const updateCoupon = async (coupon?: BillingCoupon) => {
    if (!isAdmin) return;
    setIntroCoupon(coupon);
    if (coupon?.id === family.billingInfo?.introCoupon?.id) {
      return;
    }
    const resp = await requestUpdateFamily(
      new UpdateFamilyRequest({
        familyRef: family.ref,
        ...(coupon ? { updatedIntroCoupon: coupon } : { nullifyCoupon: true }),
      }),
    );
    if (resp?.family) {
      pushSnack({
        message: "Discount updated successfully",
        saveNotification: true,
        key: new Date().getTime(),
      });
      await refreshFamily();
    }
  };

  const product = localFamily.billingInfo?.product;
  const productName = product?.name || "No plan selected";
  const isMobile = useIsMobile();
  const productPrice = product
    ? `$${(product.amountCents / 100).toFixed(0)}/mo`
    : "";

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        <Typography variant="h3">Plan management</Typography>

        {/* Current plan */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h4">
              {productName} - {productPrice}
            </Typography>
            {product?.description && (
              <Typography variant="body">{product?.description}</Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Button
              variant={isMobile ? "text" : "outlined"}
              onClick={() => setChangePlanDialogOpen(true)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Change plan
            </Button>
          </Box>
        </Box>

        {/* Additional hours */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h5">Additional hours</Typography>
            <Typography variant="body">
              One-time additional hours for the current billing cycle.
            </Typography>
          </Box>
          <Button
            variant={isMobile ? "text" : "outlined"}
            onClick={() => setAddHoursDialogOpen(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            Add hours
          </Button>
        </Box>

        {/* Start date, Platform fee and Discount */}
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <DatePicker
            sx={{
              width: isMobile ? "100%" : "calc(33% - 8px)",
              ".MuiButtonBase-root": { marginRight: "unset" },
            }}
            label="Starting date"
            value={startDate}
            disabled={true}
            format="MM/dd/yyyy"
          />
          <PlatformFeeSelect
            value={platformFeeSchedule}
            onUpdate={updatePlatformFee}
            disabled={!isAdmin || updateLoading}
            fullWidth={isMobile ? true : false}
          />
          <CouponSelect
            value={introCoupon}
            onUpdate={updateCoupon}
            stacked={isMobile ? true : false}
            disabled={!isAdmin || updateLoading}
            isAdmin={isAdmin}
            widthPercentage={isMobile ? 100 : 33}
            initialCouponId={introCoupon?.id}
          />
        </Box>

        {/* End service */}
        {family.status !== FamilyStatus.DEACTIVATED &&
          family.status !== FamilyStatus.DIGITAL_DEACTIVATED &&
          family.status !== FamilyStatus.DIGITAL_DEACTIVATED_TRIAL &&
          !family.endDate && (
            <Box>
              <DropdownButton
                variant="outlined"
                options={[
                  {
                    label: "End service",
                    selectedTitle: "End service",
                    value: "end",
                  },
                  {
                    label: "Pause",
                    selectedTitle: "Pause",
                    value: "pause",
                  },
                ]}
                onClick={(value) => {
                  if (value === "end") {
                    setDeactivateDialogOpen(true);
                  } else if (value === "pause") {
                    setPauseDialogOpen(true);
                  }
                }}
                sx={{
                  "& .MuiButton-root": {
                    color: "error.main",
                    borderColor: "error.main",
                    "&:hover": {
                      backgroundColor: "transparent",
                      borderColor: "error.main",
                    },
                  },
                }}
              />
            </Box>
          )}
      </Box>

      <ChangePlanDialog
        familyRef={family.ref}
        open={changePlanDialogOpen}
        setOpen={setChangePlanDialogOpen}
        onUpdate={onPlanUpdate}
      />
      <ChangePlanDialog
        familyRef={family.ref}
        open={pauseDialogOpen}
        setOpen={setPauseDialogOpen}
        onUpdate={onPlanUpdate}
        mode="pause"
      />
      <AddHoursDialog
        familyRef={family.ref}
        open={addHoursDialogOpen}
        setOpen={setAddHoursDialogOpen}
        onUpdate={onAddHoursUpdate}
      />
      <DeactivationDialog
        familyRef={family.ref}
        open={deactivateDialogOpen}
        setOpen={setDeactivateDialogOpen}
        onUpdate={onDeactivate}
      />
    </>
  );
};
